import type { TableColumn } from "@onengine/component-library";
import type { TableFilterColumnConfig } from "@/hooks/table/useTableFilterOptions";

/**
 * A filter column has usable options only when its chip can actually be
 * populated: a `date`/`datetime`/`numerical` chip is self-contained, while a
 * `list`/`badge` chip needs a real source — an async dropdown
 * (`searchParams`/`infiniteScroll`) or hardcoded `staticItems`. A `list` config
 * with none of those would render an empty chip, so it counts as not filterable.
 */
export function hasFilterSource(config: TableFilterColumnConfig): boolean {
  const variant = config.variant ?? "list";
  if (variant === "date" || variant === "datetime" || variant === "numerical") return true;
  return Boolean(config.searchParams || config.infiniteScroll || config.staticItems?.length);
}

/**
 * Stamps `filterable`/`filterVariant`/`filterSelectionMode` onto display columns
 * from the filter-config array, which is the single source of truth for which
 * columns are filterable. A column gets a chip only when a matching config exists
 * AND that config has a usable option source — so a column with no API and no
 * hardcoded options never renders an empty filter chip.
 */
export function applyColumnFilters<T extends Record<string, unknown>>(
  columns: TableColumn<T>[],
  filterConfigs: TableFilterColumnConfig[],
): TableColumn<T>[] {
  const configByColumnId = new Map(filterConfigs.map((c) => [c.columnId, c]));
  return columns.map((col) => {
    const config = configByColumnId.get(String(col.key));
    if (!config || !hasFilterSource(config)) return col;
    return {
      ...col,
      filterable: true,
      filterVariant: config.variant ?? "list",
      filterSelectionMode: col.filterSelectionMode ?? "multi",
    };
  });
}
